import React, { useState, useEffect, useRef } from 'react';
import { 
    Radio, Users, Play, Pause, Volume2, VolumeX, Maximize2, 
    Heart, Sparkles, ShoppingBag, Check, Globe, MapPin, 
    ChevronDown, ChevronUp, ArrowRight, ArrowLeft, Clock, Flame, 
    ShieldCheck, Eye, Share2, X, Bell, ExternalLink, AlertCircle
} from 'lucide-react';
import { liveAPI, getProducts } from '../api/client';
import { getCurrentLanguage, setLanguage, isRTL, SUPPORTED_LANGUAGES } from '../utils/translations';
import { useNavigate } from 'react-router-dom';
import Hls from 'hls.js';

const LIVE_I18N = {
    ar: {
        broadcastRegion: "منطقة البث المعتمدة حالياً:",
        switchCountry: "[ تغيير دولة البث ▾ ]",
        liveBadge: "بث مباشر حي الآن 👑",
        studioBadge: "استوديو البث المباشر الملكي 👑",
        heroTitle: "مسرح البث المباشر والبيع الحصري الفاخر",
        heroSubtitle: "شاهد العروض الحية الحصرية، تسوق القطع الملكية النادرة بخصومات البث المباشر الفورية، وتفاعل في الوقت الفعلي مع المذيع وخبراء الموضة.",
        noStreamTitle: "لا يوجد بث مباشر نشط حالياً",
        noStreamDesc: "استوديو البث مغلق حالياً. يرجى مراجعة جدول العروض القادمة أدناه أو استكشاف أحدث المجموعات الحصرية في متجرنا.",
        browseStore: "تصفح متجر لوكسفونو الرئيسي",
        catalogTitle: "مقتنيات الكاتالوج الفاخرة المعتمدة",
        catalogDesc: "الأسعار معتمدة ومحدثة لعملاء بالعملة الرسمية",
        instantBuy: "شراء فوري ⚡",
        addToCart: "إضافة للسلة",
        scheduleTitle: "جدول عروض البث المباشر القادمة للمنطقة",
        remindMe: "تذكيري",
        selectCountryTitle: "اختر دولة البث والعملة",
        selectCountryDesc: "يتم تخصيص البث المباشر والمنتجات وأسعار العملات والشحن وفقاً لدولتك المختارة:",
        reminderSuccess: "🔔 تم تفعيل التذكير بالبث بنجاح!",
        cartTitle: "السلة",
        changeLang: "تغيير اللغة"
    },
    en: {
        broadcastRegion: "Current Authorized Broadcast Region:",
        switchCountry: "[ Switch Country ▾ ]",
        liveBadge: "EXCLUSIVE VIP LIVE BROADCAST 👑",
        studioBadge: "LUXVONO ROYAL LIVE STUDIO 👑",
        heroTitle: "Luxury Live Stream & VIP Showroom",
        heroSubtitle: "Experience live runway broadcasts, purchase rare luxury pieces with exclusive live discounts, and interact directly with host stylists.",
        noStreamTitle: "No Live Broadcast Currently Active",
        noStreamDesc: "The live studio is currently offline. Please check the upcoming exhibition schedule below or explore our luxury collections in the main catalog.",
        browseStore: "Browse LuxVono Main Store",
        catalogTitle: "Featured Luxury Catalog Collection",
        catalogDesc: "Official certified prices for clients in official currency",
        instantBuy: "Instant Buy ⚡",
        addToCart: "Add to Bag",
        scheduleTitle: "Upcoming VIP Scheduled Broadcasts",
        remindMe: "Remind Me",
        selectCountryTitle: "Select Broadcast Country & Currency",
        selectCountryDesc: "Live broadcasts, products, currency rates, and instant shipping are customized for your selected country:",
        reminderSuccess: "🔔 Reminder notification set successfully!",
        cartTitle: "Cart",
        changeLang: "Change Language"
    },
    fr: {
        broadcastRegion: "Région de diffusion autorisée :",
        switchCountry: "[ Changer de pays ▾ ]",
        liveBadge: "DIFFUSION VIP EN DIRECT 👑",
        studioBadge: "STUDIO EN DIRECT ROYAL LUXVONO 👑",
        heroTitle: "Théâtre de Diffusion en Direct & Salon VIP",
        heroSubtitle: "Assistez aux défilés en direct, achetez des pièces rares avec réductions immédiates et échangez avec les experts.",
        noStreamTitle: "Aucune diffusion en direct pour le moment",
        noStreamDesc: "Le studio en direct est actuellement hors ligne. Consultez le calendrier des présentations ci-dessous ou explorez nos collections exclusives.",
        browseStore: "Visiter la boutique LuxVono",
        catalogTitle: "Collections de Luxe Certifiées",
        catalogDesc: "Tarifs officiels et garantis dans la devise locale",
        instantBuy: "Achat Immédiat ⚡",
        addToCart: "Ajouter au panier",
        scheduleTitle: "Prochaines Diffusions VIP Programmées",
        remindMe: "Me rappeler",
        selectCountryTitle: "Sélectionner le Pays de Diffusion et la Devise",
        selectCountryDesc: "Les flux en direct, articles, devises et livraisons sont adaptés à votre pays :",
        reminderSuccess: "🔔 Rappel activé avec succès !",
        cartTitle: "Panier",
        changeLang: "Changer de langue"
    },
    de: {
        broadcastRegion: "Autorisierte Übertragungsregion:",
        switchCountry: "[ Land wechseln ▾ ]",
        liveBadge: "EXKLUSIVER VIP LIVE-STREAM 👑",
        studioBadge: "LUXVONO KÖNIGLICHES LIVE-STUDIO 👑",
        heroTitle: "Luxus Live-Stream & VIP Showroom",
        heroSubtitle: "Erleben Sie Live-Präsentationen, erwerben Sie seltene Sammlerstücke mit Live-Rabatten und interagieren Sie direkt mit Experten.",
        noStreamTitle: "Zurzeit kein aktiver Live-Stream",
        noStreamDesc: "Das Live-Studio ist offline. Entdecken Sie den Programmplan unten oder stöbern Sie in unseren Kollektionen.",
        browseStore: "Zum LuxVono Store",
        catalogTitle: "Zertifizierte Luxus-Kollektion",
        catalogDesc: "Offizielle Preise für Kunden in der Landeswährung",
        instantBuy: "Sofortkauf ⚡",
        addToCart: "In den Warenkorb",
        scheduleTitle: "Bevorstehende VIP-Übertragungen",
        remindMe: "Erinnern",
        selectCountryTitle: "Sendeland & Währung auswählen",
        selectCountryDesc: "Streams, Produkte, Währungen und Versand werden an Ihr Land angepasst:",
        reminderSuccess: "🔔 Erinnerung erfolgreich eingerichtet!",
        cartTitle: "Warenkorb",
        changeLang: "Sprache wechseln"
    },
    es: {
        broadcastRegion: "Región de transmisión autorizada:",
        switchCountry: "[ Cambiar país ▾ ]",
        liveBadge: "TRANSMISIÓN VIP EN VIVO 👑",
        studioBadge: "ESTUDIO REAL EN VIVO LUXVONO 👑",
        heroTitle: "Teatro de Transmisión en Vivo y Showroom VIP",
        heroSubtitle: "Disfrute de presentaciones en directo, adquiera piezas raras con descuentos exclusivos e interactúe con estilistas.",
        noStreamTitle: "No hay transmisiones en vivo activas en este momento",
        noStreamDesc: "El estudio está temporalmente fuera de línea. Consulte el calendario de transmisiones o explore nuestras colecciones.",
        browseStore: "Explorar Tienda LuxVono",
        catalogTitle: "Colecciones de Lujo Certificadas",
        catalogDesc: "Precios oficiales actualizados en su divisa local",
        instantBuy: "Compra Inmediata ⚡",
        addToCart: "Añadir a la cesta",
        scheduleTitle: "Próximas transmisiones VIP programadas",
        remindMe: "Recordarme",
        selectCountryTitle: "Seleccionar País de Transmisión y Moneda",
        selectCountryDesc: "Transmisiones, productos, tipos de cambio y envíos adaptados a su país:",
        reminderSuccess: "🔔 ¡Recordatorio activado con éxito!",
        cartTitle: "Cesta",
        changeLang: "Cambiar idioma"
    },
    it: {
        broadcastRegion: "Regione di trasmissione autorizzata:",
        switchCountry: "[ Cambia paese ▾ ]",
        liveBadge: "DIRETTA VIP ESCLUSIVA 👑",
        studioBadge: "STUDIO LIVE REALE LUXVONO 👑",
        heroTitle: "Teatro in Diretta Streaming & VIP Showroom",
        heroSubtitle: "Segui le sfilate in diretta, acquista pezzi rari con sconti dedicati e interagisci con stilisti ed esperti.",
        noStreamTitle: "Nessuna diretta attiva al momento",
        noStreamDesc: "Lo studio è offline. Consulta il calendario degli eventi o esplora il catalogo generale.",
        browseStore: "Sfoglia Negozio LuxVono",
        catalogTitle: "Collezione di Lusso Certificata",
        catalogDesc: "Prezzi ufficiali verificati nella valuta locale",
        instantBuy: "Acquisto Immediato ⚡",
        addToCart: "Aggiungi al carrello",
        scheduleTitle: "Prossime trasmissioni VIP in programma",
        remindMe: "Ricordamelo",
        selectCountryTitle: "Seleziona Paese di Trasmissione e Valuta",
        selectCountryDesc: "Streaming, prodotti, valuta e spedizione personalizzati per il tuo paese:",
        reminderSuccess: "🔔 Promemoria impostato con successo!",
        cartTitle: "Carrello",
        changeLang: "Cambia lingua"
    },
    ru: {
        broadcastRegion: "Авторизованный регион вещания:",
        switchCountry: "[ Сменить страну ▾ ]",
        liveBadge: "ЭКСКЛЮЗИВНЫЙ VIP ПРЯМОЙ ЭФИР 👑",
        studioBadge: "КОРОЛЕВСКАЯ СТУДИЯ LUXVONO 👑",
        heroTitle: "Театр Прямого Эфира и VIP Шоурум",
        heroSubtitle: "Смотрите эксклюзивные показы, приобретайте редкие шедевры со скидками прямого эфира и общайтесь с кураторами.",
        noStreamTitle: "В данный момент нет активных трансляций",
        noStreamDesc: "Студия прямого эфира закрыта. Ознакомьтесь с расписанием предстоящих показов или откройте каталог.",
        browseStore: "Перейти в бутик LuxVono",
        catalogTitle: "Коллекция Сертифицированных Шедевров",
        catalogDesc: "Официальные цены в национальной валюте",
        instantBuy: "Купить сейчас ⚡",
        addToCart: "В корзину",
        scheduleTitle: "Расписание предстоящих VIP эфиров",
        remindMe: "Напомнить",
        selectCountryTitle: "Выберите страну вещания и валюту",
        selectCountryDesc: "Прямые эфиры, цены, валюта и доставка адаптируются под ваш регион:",
        reminderSuccess: "🔔 Напоминание успешно установлено!",
        cartTitle: "Корзина",
        changeLang: "Сменить язык"
    },
    tr: {
        broadcastRegion: "Yetkili Yayın Bölgesi:",
        switchCountry: "[ Ülke Değiştir ▾ ]",
        liveBadge: "ÖZEL VIP CANLI YAYIN 👑",
        studioBadge: "LUXVONO KRALİYET CANLI STÜDYOSU 👑",
        heroTitle: "Lüks Canlı Yayın ve VIP Gösteri Salonu",
        heroSubtitle: "Özel canlı sunumları izleyin, canlı yayın indirimleriyle nadir parçaları satın alın ve uzmanlarla etkileşime geçin.",
        noStreamTitle: "Şu Anda Aktif Canlı Yayın Bulunmuyor",
        noStreamDesc: "Canlı yayın stüdyosu kapalıdır. Aşağıdaki yayın takvimini inceleyebilir veya kataloğu keşfedebilirsiniz.",
        browseStore: "LuxVono Mağazasını Keşfet",
        catalogTitle: "Sertifikalı Lüks Katalog Koleksiyonu",
        catalogDesc: "Resmi para birimiyle sertifikalı güncel fiyatlar",
        instantBuy: "Hemen Satın Al ⚡",
        addToCart: "Sepete Ekle",
        scheduleTitle: "Bölgeniz İçin Yaklaşan VIP Yayınlar",
        remindMe: "Bana Hatırlat",
        selectCountryTitle: "Yayın Ülkesi ve Para Birimi Seçin",
        selectCountryDesc: "Canlı yayınlar, ürünler ve teslimat seçtiğiniz ülkeye göre özelleştirilir:",
        reminderSuccess: "🔔 Canlı yayın hatırlatıcısı ayarlandı!",
        cartTitle: "Sepet",
        changeLang: "Dili Değiştir"
    },
    zh: {
        broadcastRegion: "当前官方授权直播区域：",
        switchCountry: "[ 切换直播国家 ▾ ]",
        liveBadge: "VIP尊享实况直播 👑",
        studioBadge: "LUXVONO 皇家实况演播厅 👑",
        heroTitle: "皇家尊享实况直播与VIP私人鉴赏厅",
        heroSubtitle: "沉浸式鉴赏实况名品走秀，享直播专享立减礼遇，与顶级鉴赏师及名模实时互动交流。",
        noStreamTitle: "当前暂无正在进行的实况直播",
        noStreamDesc: "演播厅目前暂未开播。请查看下方的专属排期或在主精品商城中探索传世名品。",
        browseStore: "进入 LuxVono 官方商城",
        catalogTitle: "官方认证传世奢华臻品",
        catalogDesc: "采用官方当地货币实时结算与保价配送",
        instantBuy: "立即典藏 ⚡",
        addToCart: "加入购物袋",
        scheduleTitle: "官方 VIP 专场实况排期",
        remindMe: "开播提醒",
        selectCountryTitle: "选择直播国家与结算货币",
        selectCountryDesc: "实况信号、精选商品、结算汇率及专机速递将根据您所选的国家定制：",
        reminderSuccess: "🔔 开播提醒已成功设置！",
        cartTitle: "购物袋",
        changeLang: "切换语言"
    },
    ja: {
        broadcastRegion: "公式認定配信地域：",
        switchCountry: "[ 国を変更 ▾ ]",
        liveBadge: "VIP限定ライブ配信 👑",
        studioBadge: "LUXVONO ロイヤルライブスタジオ 👑",
        heroTitle: "ラグジュアリー・ライブ配信＆VIPショールーム",
        heroSubtitle: "リアルタイムで逸品のライブ配信を鑑賞し、特別価格で購入、専門家と対話をお楽しみいただけます。",
        noStreamTitle: "現在アクティブなライブ配信はありません",
        noStreamDesc: "現在スタジオはオフラインです。今後のスケジュールをご確認いただくか、カタログをご覧ください。",
        browseStore: "LuxVono ストアを見る",
        catalogTitle: "公認ラグジュアリー・コレクション",
        catalogDesc: "選択国の公式通貨による正規認定価格",
        instantBuy: "今すぐ購入 ⚡",
        addToCart: "バッグに追加",
        scheduleTitle: "配信予定のロイヤル・ライブショー",
        remindMe: "リマインダー",
        selectCountryTitle: "配信対象国と通貨の選択",
        selectCountryDesc: "配信コンテンツ、製品、通貨、配送はお客様の国に合わせて最適化されます：",
        reminderSuccess: "🔔 リマインダーを設定しました！",
        cartTitle: "カート",
        changeLang: "言語を変更"
    },
    ko: {
        broadcastRegion: "공식 승인 방송 지역:",
        switchCountry: "[ 국가 변경 ▾ ]",
        liveBadge: "VIP 단독 라이브 방송 👑",
        studioBadge: "LUXVONO 로열 라이브 스튜디오 👑",
        heroTitle: "럭셔리 라이브 스트림 & VIP 쇼룸",
        heroSubtitle: "실시간 런웨이 방송을 감상하고, 라이브 특별 혜택으로 희귀 걸작을 소장하며 전문 큐레이터와 직접 소통하세요.",
        noStreamTitle: "현재 진행 중인 라이브 방송이 없습니다",
        noStreamDesc: "현재 라이브 스튜디오가 오프라인 상태입니다. 예정된 방송 일정을 확인하시거나 카탈로그를 둘러보세요.",
        browseStore: "LuxVono 스토어 바로가기",
        catalogTitle: "인증된 하이엔드 럭셔리 컬렉션",
        catalogDesc: "선택 국가의 공식 통화로 제공되는 정품 인증 가격",
        instantBuy: "즉시 구매 ⚡",
        addToCart: "쇼핑백에 담기",
        scheduleTitle: "예정된 VIP 라이브 방송 일정",
        remindMe: "알림 받기",
        selectCountryTitle: "방송 국가 및 통화 선택",
        selectCountryDesc: "라이브 방송, 상품, 환율 및 배송 옵션이 선택한 국가에 맞춰 제공됩니다:",
        reminderSuccess: "🔔 방송 알림이 성공적으로 등록되었습니다!",
        cartTitle: "쇼핑백",
        changeLang: "언어 변경"
    },
    so: {
        broadcastRegion: "Gobolka baahinta ee la oggolaaday:",
        switchCountry: "[ Beddel Dalka ▾ ]",
        liveBadge: "BAAHIN TOOS AH EE VIP 👑",
        studioBadge: "ISTUUDIYOWGA TOOSKA AH EE LUXVONO 👑",
        heroTitle: "Masraxa Baahinta Tooska ah & Qolka VIP-da",
        heroSubtitle: "Daawo bandhigyada tooska ah, ka iibso alaabta dhifka ah qiimo dhimis gaar ah, oo la hadal khubarada.",
        noStreamTitle: "Ma jiro baahin toos ah oo hadda socota",
        noStreamDesc: "Istuudiyowga hadda wuu xiran yahay. Fadlan fiiri jadwalka hoose ama baadh buug-yaraha guud.",
        browseStore: "Booqo Dukaanka LuxVono",
        catalogTitle: "Ururinta Alaabaha Qaliga ah ee La Aqoonsan yahay",
        catalogDesc: "Qiimayaal rasmi ah oo ku habboon dalkaaga",
        instantBuy: "Iibso Hadda ⚡",
        addToCart: "Ku dar Gaariga",
        scheduleTitle: "Jadwalka Baahinta VIP ee Soo Socota",
        remindMe: "I xasuusi",
        selectCountryTitle: "Dooro Dalka Baahinta iyo Lacagta",
        selectCountryDesc: "Baahinta, alaabta, lacagaha iyo keenista waxaa lagu habeynayaa dalka aad dooratay:",
        reminderSuccess: "🔔 Xusuusinta baahinta waa la shiday!",
        cartTitle: "Gaariga",
        changeLang: "Beddel Luqadda"
    },
    am: {
        broadcastRegion: "የተፈቀደ የቀጥታ ስርጭት ክልል፦",
        switchCountry: "[ ሀገር ቀይር ▾ ]",
        liveBadge: "ልዩ የVIP የቀጥታ ስርጭት 👑",
        studioBadge: "የLUXVONO ንጉሣዊ የቀጥታ ስቱዲዮ 👑",
        heroTitle: "የቅንጦት የቀጥታ ስርጭት እና የVIP ማሳያ አዳራሽ",
        heroSubtitle: "ልዩ የቀጥታ ትዕይንቶችን ይመልከቱ፣ ብርቅዬ የቅንጦት እቃዎችን በቅናሽ ይግዙ እና ከባለሙያዎች ጋር ይገናኙ።",
        noStreamTitle: "በአሁኑ ጊዜ ንቁ የቀጥታ ስርጭት የለም",
        noStreamDesc: "ስቱዲዮው በአሁኑ ጊዜ ዝግ ነው። ከዚህ በታች ያለውን የፕሮግራም ሰሌዳ ይመልከቱ ወይም ካታሎጉን ያስሱ።",
        browseStore: "ወደ LuxVono መደብር ይሂዱ",
        catalogTitle: "የተረጋገጡ የቅንጦት ስብስቦች",
        catalogDesc: "ለደንበኞች በይፋዊ ምንዛሬ የተረጋገጡ ዋጋዎች",
        instantBuy: "አሁን ይግዙ ⚡",
        addToCart: "ወደ ጋሪ ጨምር",
        scheduleTitle: "የሚመጡ የVIP የቀጥታ ስርጭቶች መርሃ ግብር",
        remindMe: "አስታውሰኝ",
        selectCountryTitle: "የስርጭት ሀገር እና ምንዛሬ ይምረጡ",
        selectCountryDesc: "የቀጥታ ስርጭቶች፣ እቃዎች፣ የገንዘብ ምንዛሬ እና አቅርቦት በእርስዎ ሀገር መሰረት ይስተካከላሉ፦",
        reminderSuccess: "🔔 ማሳሰቢያው በተሳካ ሁኔታ ተዘጋጅቷል!",
        cartTitle: "ጋሪ",
        changeLang: "ቋንቋ ቀይር"
    },
    gdq: {
        broadcastRegion: "منطقة البث المعتمدة حالياً باللسان المهري:",
        switchCountry: "[ تبديل البلاد ▾ ]",
        liveBadge: "بث مباشر حي الآن 👑",
        studioBadge: "استوديو البث الملكي باللسان المهري 👑",
        heroTitle: "مسرح البث المباشر والمقتنيات الفارهة",
        heroSubtitle: "شاهد عروض البث المباشر واقتنِ القطع النادرة وتفاعل مباشرة مع خبراء المقتنيات.",
        noStreamTitle: "لا يوجد بث مباشر نشط حالياً",
        noStreamDesc: "استوديو البث مغلق حالياً. يمكنك متابعة جدول العروض القادمة أدناه أو تصفح المقتنيات الفاخرة.",
        browseStore: "تصفح متجر لوكسفونو الرئيسي",
        catalogTitle: "مقتنيات الكاتالوج الفاخرة المعتمدة",
        catalogDesc: "الأسعار معتمدة ومحدثة لعملاء بالعملة الرسمية",
        instantBuy: "شراء فوري ⚡",
        addToCart: "إضافة للسلة",
        scheduleTitle: "جدول عروض البث المباشر القادمة للمنطقة",
        remindMe: "تذكيري",
        selectCountryTitle: "اختر دولة البث والعملة",
        selectCountryDesc: "يتم تخصيص البث المباشر والمنتجات وأسعار العملات والشحن وفقاً لدولتك المختارة:",
        reminderSuccess: "🔔 تم تفعيل التذكير بالبث بنجاح!",
        cartTitle: "السلة",
        changeLang: "تغيير اللغة"
    }
};

const POPULAR_REGIONS = [
    { code: 'SA', name: 'المملكة العربية السعودية', nameEn: 'Saudi Arabia', flag: '🇸🇦', currency: 'SAR', symbol: 'ر.س' },
    { code: 'AE', name: 'الإمارات العربية المتحدة', nameEn: 'United Arab Emirates', flag: '🇦🇪', currency: 'AED', symbol: 'د.إ' },
    { code: 'KW', name: 'دولة الكويت', nameEn: 'Kuwait', flag: '🇰🇼', currency: 'KWD', symbol: 'د.ك' },
    { code: 'QA', name: 'دولة قطر', nameEn: 'Qatar', flag: '🇶🇦', currency: 'QAR', symbol: 'ر.ق' },
    { code: 'OM', name: 'سلطنة عمان', nameEn: 'Oman', flag: '🇴🇲', currency: 'OMR', symbol: 'ر.ع' },
    { code: 'BH', name: 'مملكة البحرين', nameEn: 'Bahrain', flag: '🇧🇭', currency: 'BHD', symbol: 'د.ب' },
    { code: 'YE', name: 'الجمهورية اليمنية', nameEn: 'Yemen', flag: '🇾🇪', currency: 'USD', symbol: '$' },
    { code: 'EG', name: 'جمهورية مصر العربية', nameEn: 'Egypt', flag: '🇪🇬', currency: 'EGP', symbol: 'ج.م' },
    { code: 'US', name: 'الولايات المتحدة الأمريكية', nameEn: 'United States', flag: '🇺🇸', currency: 'USD', symbol: '$' },
    { code: 'GB', name: 'المملكة المتحدة', nameEn: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', symbol: '£' },
    { code: 'GLOBAL', name: 'كافة دول العالم (بث دولي)', nameEn: 'Worldwide (Global Stream)', flag: '🌍', currency: 'USD', symbol: '$' }
];

const LivePortalPage = ({ onAddToCart, onBuyNow, cartCount = 0 }) => {
    const navigate = useNavigate();

    // 1. Language State
    const [currentLang, setCurrentLang] = useState(() => getCurrentLanguage() || 'ar');
    const isRtl = currentLang === 'ar' || currentLang === 'gdq' || currentLang === 'fa' || currentLang === 'ur';
    const strings = LIVE_I18N[currentLang] || LIVE_I18N['en'] || LIVE_I18N['ar'];

    // 2. Country / Region State ("ولبلاد الذي يظهر فيه البث")
    const [selectedRegion, setSelectedRegion] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_geo_context');
            if (stored) {
                const parsed = JSON.parse(stored);
                const match = POPULAR_REGIONS.find(r => r.code === parsed.countryCode || r.code === parsed.country);
                if (match) return match;
            }
        } catch (_) {}
        return POPULAR_REGIONS[0]; // Default: Saudi Arabia 🇸🇦
    });

    // Modals
    const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
    const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

    // Live Stream Real Data
    const [rooms, setRooms] = useState([]);
    const [activeRoomId, setActiveRoomId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [liveProducts, setLiveProducts] = useState([]);
    const [notificationToast, setNotificationToast] = useState(null);

    // Interactive Player Controls
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [isAutoplayBlocked, setIsAutoplayBlocked] = useState(false);
    const [isBuffering, setIsBuffering] = useState(true);
    const [floatingHearts, setFloatingHearts] = useState([]);
    const [aspectMode, setAspectMode] = useState('portrait'); // 'portrait' (native 9:16 mobile) or 'theater' (16:9 widescreen)

    // Reset layout padding on .main-content to eliminate the white space gap
    useEffect(() => {
        const mainEl = document.querySelector('.main-content');
        const rootEl = document.querySelector('.luxvono-root');
        const originalMainPadding = mainEl ? mainEl.style.paddingTop : '';
        const originalRootBg = rootEl ? rootEl.style.backgroundColor : '';

        if (mainEl) {
            mainEl.style.paddingTop = '0px';
            mainEl.style.backgroundColor = '#090A0F';
        }
        if (rootEl) {
            rootEl.style.backgroundColor = '#090A0F';
        }
        document.body.style.backgroundColor = '#090A0F';

        return () => {
            if (mainEl) {
                mainEl.style.paddingTop = originalMainPadding;
                mainEl.style.backgroundColor = '';
            }
            if (rootEl) {
                rootEl.style.backgroundColor = '';
            }
            document.body.style.backgroundColor = '';
        };
    }, []);

    // Sync language changes from global events
    useEffect(() => {
        const handleLangChange = () => {
            setCurrentLang(getCurrentLanguage());
        };
        window.addEventListener('languageChange', handleLangChange);
        window.addEventListener('geoContextChange', handleLangChange);
        return () => {
            window.removeEventListener('languageChange', handleLangChange);
            window.removeEventListener('geoContextChange', handleLangChange);
        };
    }, []);

    // Fetch REAL active rooms & REAL catalog products
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [roomsData, prodsData] = await Promise.allSettled([
                    liveAPI.getActiveRooms(),
                    getProducts(0, 8, '', '', '', '', selectedRegion.code)
                ]);

                if (roomsData.status === 'fulfilled') {
                    const activeList = Array.isArray(roomsData.value) ? roomsData.value.filter(r => r.status === 'ACTIVE' || r.status === 'LIVE' || r.status === 'PREVIEW' || r.status === 'MEDIA_CHECKING') : [];
                    setRooms(activeList);
                    if (activeList.length > 0) {
                        setActiveRoomId(prev => (prev && activeList.some(r => r.id === prev)) ? prev : activeList[0].id);
                    } else {
                        setActiveRoomId(null);
                    }
                }

                if (prodsData.status === 'fulfilled' && prodsData.value?.data) {
                    setLiveProducts(prodsData.value.data);
                }
            } catch (e) {
                console.error("Error loading live portal data:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [selectedRegion.code]);

    const activeStreamRoom = rooms.length > 0 ? (rooms.find(r => r.id === activeRoomId) || rooms[0]) : null;
    const activeRoomIdx = rooms.length > 0 ? Math.max(0, rooms.findIndex(r => r.id === (activeStreamRoom?.id ?? activeRoomId))) : 0;
    const streamUrl = activeStreamRoom?.streamUrl?.trim() || (activeStreamRoom?.streamKey ? `https://luxvono.com/media/hls/${activeStreamRoom.streamKey}.m3u8` : null);
    const realViewerCount = activeStreamRoom?.viewerCount || 0;

    // TikTok Vertical Swipe Gesture State (Up / Down between active live broadcasts)
    const portalTouchStartYRef = useRef(null);
    const portalTouchStartXRef = useRef(null);
    const portalLastWheelRef = useRef(0);
    const [portalSwipeOffsetY, setPortalSwipeOffsetY] = useState(0);

    const switchPortalLiveRoom = async (direction = 'next') => {
        setPortalSwipeOffsetY(direction === 'next' ? -45 : 45);
        setTimeout(() => setPortalSwipeOffsetY(0), 220);

        let currentList = rooms;
        if (currentList.length <= 1) {
            try {
                const fetched = await liveAPI.getActiveRooms();
                const activeList = Array.isArray(fetched)
                    ? fetched.filter(r => r.status === 'ACTIVE' || r.status === 'LIVE' || r.status === 'PREVIEW' || r.status === 'MEDIA_CHECKING')
                    : [];
                if (activeList.length > 0) {
                    setRooms(activeList);
                    currentList = activeList;
                }
            } catch (_) {}
        }

        if (currentList.length <= 1) {
            showToast(
                isRtl
                    ? '📡 هذا هو البث المباشر الوحيد النشط حالياً — اسحب للأعلى أو الأسفل فور دخول بث آخر'
                    : '📡 Only 1 live stream is currently active — swipe up/down when another host goes live'
            );
            return;
        }

        const currIdx = Math.max(0, currentList.findIndex(r => r.id === (activeStreamRoom?.id ?? activeRoomId)));
        const nextIdx = direction === 'prev'
            ? (currIdx - 1 + currentList.length) % currentList.length
            : (currIdx + 1) % currentList.length;
        const target = currentList[nextIdx];
        if (target) {
            setActiveRoomId(target.id);
            showToast(
                isRtl
                    ? `📡 البث (${nextIdx + 1}/${currentList.length}): ${target.title || target.sellerName || 'بث مباشر'}`
                    : `📡 Live (${nextIdx + 1}/${currentList.length}): ${target.title || target.sellerName || 'Live Stream'}`
            );
        }
    };

    const handlePortalTouchStart = (e) => {
        const touch = e.touches?.[0];
        if (!touch) return;
        portalTouchStartYRef.current = touch.clientY;
        portalTouchStartXRef.current = touch.clientX;
    };

    const handlePortalTouchMove = (e) => {
        if (portalTouchStartYRef.current === null) return;
        const touch = e.touches?.[0];
        if (!touch) return;
        const deltaY = touch.clientY - portalTouchStartYRef.current;
        const deltaX = Math.abs(touch.clientX - (portalTouchStartXRef.current || 0));
        if (Math.abs(deltaY) > deltaX && Math.abs(deltaY) > 12) {
            setPortalSwipeOffsetY(Math.max(-75, Math.min(75, deltaY * 0.35)));
        }
    };

    const handlePortalTouchEnd = (e) => {
        if (portalTouchStartYRef.current === null) return;
        const touch = e.changedTouches?.[0];
        const endY = touch ? touch.clientY : portalTouchStartYRef.current;
        const endX = touch ? touch.clientX : (portalTouchStartXRef.current || 0);
        const deltaY = endY - portalTouchStartYRef.current;
        const deltaX = Math.abs(endX - (portalTouchStartXRef.current || 0));
        portalTouchStartYRef.current = null;
        portalTouchStartXRef.current = null;

        if (Math.abs(deltaY) >= 55 && Math.abs(deltaY) > deltaX * 1.2) {
            if (deltaY < 0) {
                switchPortalLiveRoom('next');
            } else {
                switchPortalLiveRoom('prev');
            }
        } else {
            setPortalSwipeOffsetY(0);
        }
    };

    const handlePortalWheel = (e) => {
        const now = Date.now();
        if (now - portalLastWheelRef.current < 700) return;
        if (Math.abs(e.deltaY) >= 38) {
            portalLastWheelRef.current = now;
            if (e.deltaY > 0) {
                switchPortalLiveRoom('next');
            } else {
                switchPortalLiveRoom('prev');
            }
        }
    };

    useEffect(() => {
        if (!videoRef.current || !streamUrl) return;

        const videoEl = videoRef.current;
        try {
            videoEl.muted = true;
            videoEl.defaultMuted = true;
            videoEl.playsInline = true;
            videoEl.setAttribute('playsinline', '');
            videoEl.setAttribute('webkit-playsinline', '');
            videoEl.setAttribute('x5-playsinline', '');
        } catch (_) {}

        setIsBuffering(true);
        let hls = null;

        const onVideoPlaying = () => {
            setIsBuffering(false);
            setIsAutoplayBlocked(false);
        };
        const onVideoWaiting = () => {
            setIsBuffering(true);
        };

        videoEl.addEventListener('playing', onVideoPlaying);
        videoEl.addEventListener('canplay', onVideoPlaying);
        videoEl.addEventListener('waiting', onVideoWaiting);

        if (Hls.isSupported()) {
            hls = new Hls({
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 8,
                maxLiveSyncPlaybackRate: 1.15,
                maxBufferLength: 8,
                maxMaxBufferLength: 16,
                backBufferLength: 0,
                lowLatencyMode: true,
                enableWorker: false,
                manifestLoadingMaxRetry: 25,
                manifestLoadingRetryDelay: 500,
                levelLoadingMaxRetry: 25,
                levelLoadingRetryDelay: 500,
                fragLoadingMaxRetry: 25,
                fragLoadingRetryDelay: 400,
            });
            hls.loadSource(streamUrl);
            hls.attachMedia(videoEl);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (videoRef.current) {
                    videoRef.current.muted = true;
                    const p = videoRef.current.play();
                    if (p !== undefined) {
                        p.then(() => {
                            setIsPlaying(true);
                            setIsAutoplayBlocked(false);
                        }).catch(err => {
                            console.warn("Mobile autoplay restricted by browser policy:", err);
                            setIsAutoplayBlocked(true);
                        });
                    }
                }
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.warn("Hls network error, recovering...", data);
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.warn("Hls media error, recovering...", data);
                            hls.recoverMediaError();
                            break;
                        default:
                            setTimeout(() => {
                                try { hls.loadSource(streamUrl); } catch(e) {}
                            }, 1500);
                            break;
                    }
                }
            });
        } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS for Safari / iOS Mobile
            videoEl.src = streamUrl;
            videoEl.muted = true;
            const onMeta = () => {
                const p = videoEl.play();
                if (p !== undefined) {
                    p.then(() => {
                        setIsPlaying(true);
                        setIsAutoplayBlocked(false);
                    }).catch(err => {
                        console.warn("iOS Safari autoplay prevented:", err);
                        setIsAutoplayBlocked(true);
                    });
                }
            };
            videoEl.addEventListener('loadedmetadata', onMeta, { once: true });
        }

        // Register room viewer presence
        if (activeStreamRoom?.id) {
            liveAPI.joinRoom(activeStreamRoom.id, 'متابع VIP', false).catch(() => {});
        }

        return () => {
            if (activeStreamRoom?.id) {
                liveAPI.leaveRoom(activeStreamRoom.id).catch(() => {});
            }
            if (videoEl) {
                videoEl.removeEventListener('playing', onVideoPlaying);
                videoEl.removeEventListener('canplay', onVideoPlaying);
                videoEl.removeEventListener('waiting', onVideoWaiting);
                try {
                    videoEl.pause();
                    videoEl.removeAttribute('src');
                    videoEl.load();
                } catch (e) {}
            }
            if (hls) {
                hls.destroy();
            }
        };
    }, [streamUrl, activeStreamRoom?.id]);

    // Handle Region / Country Change
    const handleSelectRegion = (reg) => {
        setSelectedRegion(reg);
        try {
            const stored = localStorage.getItem('luxvono_geo_context');
            const ctx = stored ? JSON.parse(stored) : {};
            ctx.countryCode = reg.code;
            ctx.country = reg.code;
            ctx.countryName = reg.nameEn;
            ctx.currency = reg.currency;
            localStorage.setItem('luxvono_geo_context', JSON.stringify(ctx));
            window.dispatchEvent(new Event('geoContextChange'));
            window.dispatchEvent(new Event('currencyChange'));
        } catch (_) {}
        setIsRegionModalOpen(false);
        showToast(isRtl ? `تم تحديث منطقة البث إلى: ${reg.name}` : `Broadcast region switched to: ${reg.nameEn}`);
    };

    // Handle Language Change
    const handleSelectLang = (code) => {
        setLanguage(code);
        setCurrentLang(code);
        setIsLangDropdownOpen(false);
        const langObj = SUPPORTED_LANGUAGES.find(l => l.code === code);
        showToast(langObj ? `✓ ${langObj.nativeName}` : `Language: ${code}`);
    };

    // Helper Toast
    const showToast = (msg) => {
        setNotificationToast(msg);
        setTimeout(() => setNotificationToast(null), 3500);
    };

    // Format currency according to selected region
    const formatCurrency = (amount) => {
        try {
            return new Intl.NumberFormat(isRtl ? 'ar-SA' : 'en-US', {
                style: 'currency',
                currency: selectedRegion.currency,
                maximumFractionDigits: 0
            }).format(amount);
        } catch (_) {
            return `${Math.round(amount)} ${selectedRegion.symbol}`;
        }
    };

    // Floating Hearts Reaction
    const triggerHeart = (emoji = '💖') => {
        const id = Date.now() + Math.random();
        const startX = 20 + Math.random() * 60;
        setFloatingHearts(prev => [...prev.slice(-15), { id, emoji, startX }]);
        setTimeout(() => {
            setFloatingHearts(prev => prev.filter(h => h.id !== id));
        }, 2200);
    };

    // Video controls
    // Video controls
    const togglePlay = () => {
        if (!videoRef.current) return;
        if (isPlaying) {
            videoRef.current.pause();
            setIsPlaying(false);
        } else {
            videoRef.current.play().then(() => {
                setIsPlaying(true);
                setIsAutoplayBlocked(false);
            }).catch(console.error);
        }
    };

    const handleUserUnlockPlay = (e) => {
        if (e) e.stopPropagation();
        if (!videoRef.current) return;
        videoRef.current.muted = false;
        setIsMuted(false);
        videoRef.current.play()
            .then(() => {
                setIsPlaying(true);
                setIsAutoplayBlocked(false);
                setIsBuffering(false);
                showToast(isRtl ? "🔊 تم تفعيل الصوت المباشر" : "🔊 Live Audio Enabled");
            })
            .catch(err => {
                console.warn("Unmuted play blocked, attempting muted fallback:", err);
                videoRef.current.muted = true;
                setIsMuted(true);
                videoRef.current.play().then(() => {
                    setIsPlaying(true);
                    setIsAutoplayBlocked(false);
                }).catch(console.error);
            });
    };

    const toggleAudio = (e) => {
        if (e) e.stopPropagation();
        if (!videoRef.current) return;
        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(videoRef.current.muted);
        if (!videoRef.current.muted) {
            showToast(isRtl ? "🔊 تم تشغيل الصوت الحي" : "🔊 Live Audio Enabled");
        }
        if (videoRef.current.paused) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
        }
    };

    // Active real room setup is now handled before useEffect

    return (
        <div 
            className="live-portal-root"
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{
                background: '#090A0F',
                color: '#FFFFFF',
                minHeight: '100vh',
                fontFamily: "'Outfit', 'Noto Sans Arabic', 'Inter', sans-serif",
                position: 'relative',
                overflowX: 'hidden'
            }}
        >
            <style dangerouslySetInnerHTML={{__html: `
                .live-portal-root {
                    background: radial-gradient(circle at 50% 0%, #1a1429 0%, #090a0f 60%) !important;
                }
                .live-top-navbar {
                    position: sticky;
                    top: 0;
                    z-index: 999;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 6%;
                    background: rgba(9, 10, 15, 0.85);
                    backdrop-filter: blur(25px);
                    -webkit-backdrop-filter: blur(25px);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }
                .live-nav-brand {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    cursor: pointer;
                    text-decoration: none;
                }
                .live-brand-title {
                    font-size: 22px;
                    font-weight: 800;
                    letter-spacing: 4px;
                    background: linear-gradient(135deg, #FFFFFF 40%, #E2E8F0 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .live-nav-actions {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .live-nav-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    padding: 8px 16px;
                    border-radius: 40px;
                    color: #FFFFFF;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.25s ease;
                }
                .live-nav-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: rgba(255, 255, 255, 0.3);
                    transform: translateY(-1px);
                }
                .live-pulse-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(239, 68, 68, 0.15);
                    border: 1px solid rgba(239, 68, 68, 0.4);
                    color: #EF4444;
                    padding: 6px 14px;
                    border-radius: 30px;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1px;
                }
                .live-pulse-dot {
                    width: 7px;
                    height: 7px;
                    background: #EF4444;
                    border-radius: 50%;
                    box-shadow: 0 0 10px #EF4444;
                    animation: pulseDot 1.5s infinite;
                }
                @keyframes pulseDot {
                    0% { transform: scale(0.9); opacity: 1; }
                    50% { transform: scale(1.4); opacity: 0.5; }
                    100% { transform: scale(0.9); opacity: 1; }
                }
                .live-country-strip {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: rgba(255, 255, 255, 0.04);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 10px 20px;
                    font-size: 13px;
                    color: #E2E8F0;
                    font-weight: 500;
                    text-align: center;
                }
                .live-country-strip strong {
                    color: #FFFFFF;
                    cursor: pointer;
                    text-decoration: underline;
                }
                .live-stage-card {
                    position: relative;
                    max-width: 1100px;
                    margin: 0 auto;
                    border-radius: 28px;
                    overflow: hidden;
                    background: radial-gradient(circle at 50% 30%, #151b2e 0%, #07090e 100%);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 640px;
                    padding: 24px;
                }
                .live-stage-ambient-aura {
                    position: absolute;
                    inset: 0;
                    background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.04) 0%, rgba(30, 41, 59, 0.25) 50%, rgba(7, 9, 14, 0.95) 100%);
                    pointer-events: none;
                    z-index: 1;
                }
                .live-stage-portrait-frame {
                    position: relative;
                    height: 590px;
                    max-height: 80vh;
                    aspect-ratio: 9 / 16;
                    border-radius: 24px;
                    overflow: hidden;
                    background: #000;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.95);
                    z-index: 2;
                    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    flex-direction: column;
                }
                .live-stage-portrait-frame.theater-mode {
                    width: 100%;
                    max-width: 1050px;
                    height: 560px;
                    aspect-ratio: auto;
                }
                .live-stage-video {
                    width: 100%;
                    height: 100%;
                    min-height: 240px;
                    object-fit: contain;
                    display: block;
                    background: radial-gradient(circle at 50% 50%, #151b2e 0%, #07090e 100%);
                }
                .live-stage-portrait-frame.theater-mode .live-stage-video {
                    object-fit: cover;
                }
                @media (max-width: 768px) {
                    .live-stage-card {
                        padding: 8px;
                        min-height: auto;
                        border-radius: 20px;
                    }
                    .live-stage-portrait-frame {
                        width: 100%;
                        max-width: 100%;
                        height: 72vh;
                        max-height: 640px;
                        aspect-ratio: 9 / 16;
                        border-radius: 18px;
                    }
                    .live-stage-portrait-frame.theater-mode {
                        height: 50vh;
                    }
                }
                @media (max-width: 480px) {
                    .live-top-navbar {
                        padding: 10px 14px;
                    }
                    .live-brand-title {
                        font-size: 16px;
                        letter-spacing: 2px;
                    }
                    .live-country-strip {
                        font-size: 11px;
                        padding: 6px 12px;
                    }
                    .live-stage-card {
                        padding: 4px;
                        border-radius: 16px;
                    }
                    .live-stage-portrait-frame {
                        height: 70vh;
                        max-height: 540px;
                        border-radius: 14px;
                    }
                    .live-pinned-banner {
                        padding: 6px 10px;
                        font-size: 11.5px;
                    }
                }
                @media (min-width: 769px) and (max-width: 1024px) {
                    .live-stage-portrait-frame {
                        height: 520px;
                    }
                }
                @media (min-width: 1440px) {
                    .live-stage-portrait-frame {
                        height: 640px;
                    }
                }
                .live-stage-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(180deg, rgba(0,0,0,0.65) 0%, transparent 35%, rgba(0,0,0,0.85) 100%);
                    pointer-events: none;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    padding: 16px;
                    z-index: 3;
                }
                .live-pinned-banner {
                    background: rgba(15, 23, 42, 0.88);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: 12px;
                    padding: 8px 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    cursor: pointer;
                    margin-bottom: 8px;
                    pointer-events: auto;
                    transition: transform 0.2s ease, border-color 0.2s ease;
                }
                .live-pinned-banner:hover {
                    transform: translateY(-2px);
                    border-color: #FFFFFF;
                    background: rgba(21, 32, 54, 0.95);
                }
                .live-mode-toggle-btn {
                    background: rgba(0,0,0,0.65);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: #FFFFFF;
                    border-radius: 30px;
                    padding: 5px 11px;
                    font-size: 11px;
                    font-weight: bold;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    transition: all 0.2s ease;
                }
                .live-mode-toggle-btn:hover {
                    background: rgba(255, 255, 255, 0.12);
                    border-color: #FFFFFF;
                    color: #FFF;
                }
                .live-floating-heart {
                    position: absolute;
                    bottom: 80px;
                    font-size: 26px;
                    animation: floatUp 2.2s ease-out forwards;
                    pointer-events: none;
                }
                @keyframes floatUp {
                    0% { transform: translateY(0) scale(0.7); opacity: 1; }
                    100% { transform: translateY(-320px) scale(1.4); opacity: 0; }
                }
                .live-products-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 20px;
                    margin-top: 24px;
                }
                .live-prod-card {
                    background: rgba(18, 20, 32, 0.75);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 20px;
                    padding: 16px;
                    backdrop-filter: blur(20px);
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                }
                .live-prod-card:hover {
                    border-color: rgba(255, 255, 255, 0.25);
                    transform: translateY(-4px);
                    box-shadow: 0 12px 30px rgba(0,0,0,0.5);
                }
                .live-modal-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.75);
                    backdrop-filter: blur(12px);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }
                .live-modal-content {
                    background: #11131F;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: 24px;
                    width: 100%;
                    max-width: 480px;
                    padding: 28px;
                    color: #FFFFFF;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8);
                }
                .live-region-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 16px;
                    border-radius: 14px;
                    cursor: pointer;
                    margin-bottom: 8px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    transition: all 0.2s ease;
                }
                .live-region-item:hover, .live-region-item.selected {
                    background: rgba(255, 255, 255, 0.08);
                    border-color: rgba(255, 255, 255, 0.25);
                }
            `}} />

            {/* Notification Toast */}
            {notificationToast && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10001,
                    background: '#1F293D',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    color: '#FFFFFF',
                    padding: '12px 24px',
                    borderRadius: '40px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <Sparkles size={16} color="#FFFFFF" />
                    <span>{notificationToast}</span>
                </div>
            )}

            {/* TOP LUXURY NAVBAR */}
            <header className="live-top-navbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button 
                        onClick={() => navigate('/')}
                        className="live-nav-btn"
                        style={{ background: 'rgba(255,255,255,0.06)' }}
                    >
                        {isRtl ? <ArrowRight size={14} /> : <ArrowLeft size={14} />}
                        <span>{isRtl ? 'العودة للمتجر الرئيسي' : 'Back to Store'}</span>
                    </button>

                    <div className="live-nav-brand" onClick={() => navigate('/')}>
                        <span className="live-brand-title">LUXVONO</span>
                    </div>
                </div>

                <div className="live-nav-actions">
                    {/* Country & Region Button ("ولبلاد الذي يظهر فيه البث") */}
                    <button 
                        className="live-nav-btn" 
                        onClick={() => setIsRegionModalOpen(true)}
                        title={isRtl ? "تغيير دولة البث والعملة" : "Change Broadcast Country & Currency"}
                    >
                        <span style={{ fontSize: '16px' }}>{selectedRegion.flag}</span>
                        <span>{isRtl ? selectedRegion.name.split(' ')[0] : selectedRegion.nameEn}</span>
                        <span style={{ color: '#FFFFFF', fontSize: '11px', fontWeight: 'bold' }}>• {selectedRegion.currency}</span>
                        <ChevronDown size={14} />
                    </button>

                    {/* Language Switcher ("لغة العميل") */}
                    <div style={{ position: 'relative' }}>
                        <button 
                            className="live-nav-btn"
                            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                            title={strings.changeLang}
                        >
                            <Globe size={14} color="#FFFFFF" />
                            <span>{SUPPORTED_LANGUAGES.find(l => l.code === currentLang)?.nativeName || currentLang.toUpperCase()}</span>
                            <ChevronDown size={14} />
                        </button>

                        {isLangDropdownOpen && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                [isRtl ? 'left' : 'right']: 0,
                                marginTop: '8px',
                                background: '#131624',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                borderRadius: '16px',
                                padding: '8px',
                                width: '220px',
                                maxHeight: '380px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 15px 35px rgba(0,0,0,0.8)'
                            }}>
                                {SUPPORTED_LANGUAGES.map(lang => (
                                    <div 
                                        key={lang.code}
                                        onClick={() => handleSelectLang(lang.code)}
                                        style={{
                                            padding: '9px 12px',
                                            borderRadius: '10px',
                                            cursor: 'pointer',
                                            background: currentLang === lang.code ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                            color: '#FFFFFF',
                                            fontWeight: currentLang === lang.code ? 'bold' : '500',
                                            fontSize: '13px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                            transition: 'background 0.15s'
                                        }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '15px' }}>{lang.flag}</span>
                                            <span>{lang.nativeName}</span>
                                        </span>
                                        {currentLang === lang.code && <Check size={14} color="#FFFFFF" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Cart Button */}
                    <button 
                        onClick={() => navigate('/cart')}
                        className="live-nav-btn"
                        style={{ position: 'relative' }}
                    >
                        <ShoppingBag size={15} color="#FFFFFF" />
                        {cartCount > 0 && (
                            <span style={{
                                position: 'absolute',
                                top: '-4px',
                                [isRtl ? 'left' : 'right']: '-4px',
                                background: '#EF4444',
                                color: '#FFF',
                                borderRadius: '50%',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {cartCount}
                            </span>
                        )}
                    </button>
                </div>
            </header>

            {/* REGIONAL BROADCAST INFORMATION STRIP */}
            <div className="live-country-strip">
                <MapPin size={15} color="#FFFFFF" />
                <span>
                    {`${strings.broadcastRegion} ${selectedRegion.flag} ${isRtl ? selectedRegion.name : selectedRegion.nameEn}`}
                </span>
                <strong onClick={() => setIsRegionModalOpen(true)}>
                    {strings.switchCountry}
                </strong>
            </div>

            {/* MAIN CONTENT WRAPPER */}
            <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '36px 6% 80px' }}>
                
                {/* HERO TITLE & INTRO */}
                <div style={{ textAlign: 'center', maxWidth: '850px', margin: '0 auto 36px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        {activeStreamRoom ? (
                            <span className="live-pulse-badge">
                                <span className="live-pulse-dot"></span>
                                {strings.liveBadge}
                            </span>
                        ) : (
                            <span className="live-nav-btn" style={{ borderColor: 'rgba(255, 255, 255, 0.25)', color: '#FFFFFF' }}>
                                <Radio size={14} />
                                {strings.studioBadge}
                            </span>
                        )}
                    </div>
                    <h1 style={{ 
                        fontSize: 'clamp(28px, 4.5vw, 46px)', 
                        fontWeight: 900, 
                        lineHeight: 1.25, 
                        marginBottom: '16px',
                        background: 'linear-gradient(135deg, #FFFFFF 40%, #CBD5E1 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>
                        {strings.heroTitle}
                    </h1>
                    <p style={{ color: '#94A3B8', fontSize: '15px', lineHeight: 1.6, margin: '0 auto' }}>
                        {strings.heroSubtitle}
                    </p>
                </div>

                
                {rooms.length > 1 && (
                    <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', marginBottom: '20px', padding: '0 10px' }}>
                        {rooms.map(room => (
                            <button
                                key={room.id}
                                onClick={() => setActiveRoomId(room.id)}
                                style={{
                                    background: activeRoomId === room.id ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                                    border: `1px solid ${activeRoomId === room.id ? '#FFFFFF' : 'rgba(255, 255, 255, 0.1)'}`,
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    color: '#FFFFFF',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {activeRoomId === room.id && <span className="live-pulse-dot" style={{width: '6px', height: '6px'}}></span>}
                                {room.title || "بث مباشر"}
                            </button>
                        ))}
                    </div>
                )}
                {/* THE LIVE STAGE */}
                {activeStreamRoom && streamUrl ? (
                    <div className="live-stage-card">
                        <div className="live-stage-ambient-aura"></div>

                        <div
                            className={`live-stage-portrait-frame ${aspectMode === 'theater' ? 'theater-mode' : ''}`}
                            onTouchStart={handlePortalTouchStart}
                            onTouchMove={handlePortalTouchMove}
                            onTouchEnd={handlePortalTouchEnd}
                            onWheel={handlePortalWheel}
                            style={{
                                transform: portalSwipeOffsetY !== 0 ? `translateY(${portalSwipeOffsetY}px)` : 'translateY(0)',
                                transition: portalTouchStartYRef.current === null ? 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)' : 'none'
                            }}
                        >
                            {/* Floating TikTok-Style Vertical Room Switcher Pill (Up/Down) */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    [isRtl ? 'left' : 'right']: '12px',
                                    transform: 'translateY(-50%)',
                                    zIndex: 15,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '5px',
                                    background: 'rgba(9, 10, 15, 0.76)',
                                    backdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '22px',
                                    padding: '6px 4px',
                                    pointerEvents: 'auto',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)'
                                }}
                                title={isRtl ? 'مرر للأعلى أو الأسفل للتنقل بين البثوث المباشرة' : 'Swipe up or down to switch live streams'}
                            >
                                <button
                                    type="button"
                                    onClick={() => switchPortalLiveRoom('prev')}
                                    style={{
                                        width: '32px',
                                        height: '28px',
                                        borderRadius: '14px',
                                        border: 'none',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        color: '#FFFFFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <ChevronUp size={17} />
                                </button>
                                <span style={{ fontSize: '9.5px', fontWeight: '800', color: '#FFFFFF', padding: '1px 4px' }}>
                                    {`${activeRoomIdx + 1}/${Math.max(1, rooms.length)}`}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => switchPortalLiveRoom('next')}
                                    style={{
                                        width: '32px',
                                        height: '28px',
                                        borderRadius: '14px',
                                        border: 'none',
                                        background: 'rgba(254, 44, 85, 0.85)',
                                        color: '#FFFFFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <ChevronDown size={17} />
                                </button>
                            </div>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted={isMuted}
                                className="live-stage-video"
                                onClick={(e) => {
                                    if (isAutoplayBlocked || !isPlaying) {
                                        handleUserUnlockPlay(e);
                                    } else {
                                        triggerHeart('💖');
                                    }
                                }}
                            />

                            {/* Floating Hearts in Stage */}
                            {floatingHearts.map(heart => (
                                <span
                                    key={heart.id}
                                    className="live-floating-heart"
                                    style={{ [isRtl ? 'right' : 'left']: `${heart.startX}%` }}
                                >
                                    {heart.emoji}
                                </span>
                            ))}

                            {/* OVERLAY CONTROLS & HUD */}
                            <div className="live-stage-overlay">
                                {/* Top HUD */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pointerEvents: 'auto', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div className="live-pulse-badge" style={{ background: '#EF4444', color: '#FFF' }}>
                                            <span className="live-pulse-dot" style={{ background: '#FFF' }}></span>
                                            <span>LIVE</span>
                                        </div>
                                        <div style={{
                                            background: 'rgba(0,0,0,0.65)',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                            borderRadius: '30px',
                                            padding: '4px 10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            fontSize: '11px',
                                            color: '#FFF'
                                        }}>
                                            <Eye size={12} color="#FFFFFF" />
                                            <span>{Math.max(1, realViewerCount)} {isRtl ? 'مشاهد' : 'Viewers'}</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <button
                                            onClick={() => setAspectMode(prev => prev === 'portrait' ? 'theater' : 'portrait')}
                                            className="live-mode-toggle-btn"
                                            title={aspectMode === 'portrait' ? (isRtl ? 'التبديل إلى المسرح العريض' : 'Switch to Widescreen') : (isRtl ? 'التبديل إلى كاميرا الجوال 9:16' : 'Switch to Mobile 9:16')}
                                        >
                                            {aspectMode === 'portrait' ? '📱 9:16' : '📺 16:9'}
                                        </button>

                                        <button
                                            onClick={toggleAudio}
                                            style={{
                                                background: isMuted ? '#FFFFFF' : 'rgba(0,0,0,0.65)',
                                                color: isMuted ? '#090A0F' : '#FFF',
                                                border: '1px solid rgba(255,255,255,0.3)',
                                                borderRadius: '30px',
                                                padding: '5px 12px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                boxShadow: isMuted ? '0 0 15px rgba(255,255,255,0.4)' : 'none',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {isMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                                            <span>{isMuted ? (isRtl ? '🔊 تشغيل الصوت' : '🔊 Sound') : (isRtl ? 'كتم' : 'Mute')}</span>
                                        </button>

                                        <button
                                            onClick={togglePlay}
                                            style={{
                                                background: 'rgba(0,0,0,0.65)',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                borderRadius: '50%',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#FFF',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {isPlaying ? <Pause size={13} /> : <Play size={13} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Center Interactive Unlock Button for Mobile Browsers */}
                                {(isAutoplayBlocked || !isPlaying) && (
                                    <div 
                                        onClick={handleUserUnlockPlay}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'auto',
                                            cursor: 'pointer',
                                            zIndex: 10,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '12px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <div style={{
                                            width: '76px',
                                            height: '76px',
                                            borderRadius: '50%',
                                            background: '#FFFFFF',
                                            boxShadow: '0 0 35px rgba(255, 255, 255, 0.4), 0 0 20px rgba(239, 68, 68, 0.5)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Play size={36} fill="#090A0F" color="#090A0F" style={{ marginLeft: isRtl ? '0' : '4px' }} />
                                        </div>
                                        <span style={{
                                            background: 'rgba(9, 10, 15, 0.9)',
                                            border: '1.5px solid rgba(255, 255, 255, 0.3)',
                                            color: '#FFFFFF',
                                            padding: '8px 18px',
                                            borderRadius: '25px',
                                            fontSize: '13px',
                                            fontWeight: '800',
                                            backdropFilter: 'blur(10px)',
                                            boxShadow: '0 6px 20px rgba(0,0,0,0.6)',
                                            letterSpacing: '0.3px'
                                        }}>
                                            {isRtl ? 'اضغط لتشغيل البث المباشر والصوت 🔴' : 'Tap to Play Live Stream & Sound 🔴'}
                                        </span>
                                    </div>
                                )}

                                {/* Luxury Buffering Aura */}
                                {isBuffering && !isAutoplayBlocked && isPlaying && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        pointerEvents: 'none',
                                        zIndex: 9,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        <div className="live-pulse-dot" style={{ width: '22px', height: '22px', background: '#EF4444' }}></div>
                                        <span style={{ color: '#FFFFFF', fontSize: '11px', fontWeight: 'bold' }}>
                                            {isRtl ? 'جاري استقبال إشارة البث المباشر...' : 'Connecting live signal...'}
                                        </span>
                                    </div>
                                )}

                                {/* Bottom Presenter & Pinned Product Strip */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', pointerEvents: 'auto' }}>
                                    {liveProducts.length > 0 && (
                                        <div 
                                            className="live-pinned-banner" 
                                            onClick={() => {
                                                if (onAddToCart) onAddToCart(liveProducts[0]);
                                                triggerHeart('💎');
                                                showToast(`🛒 ${strings.addToCart}: ${liveProducts[0].name}`);
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                                                <span style={{ background: '#EF4444', color: '#FFF', padding: '2px 6px', borderRadius: '5px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    ⚡ {isRtl ? 'عرض مباشر' : 'Live Deal'}
                                                </span>
                                                <span style={{ fontSize: '12px', color: '#FFF', fontWeight: 'bold', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                    {liveProducts[0].name}
                                                </span>
                                            </div>
                                            <span style={{ color: '#FFFFFF', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                {formatCurrency(liveProducts[0].price)} ⚡
                                            </span>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '10px' }}>
                                        <div>
                                            <span style={{ color: '#FFFFFF', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                LUXVONO VIP Studio • {activeStreamRoom.sellerName || (isRtl ? 'بث مباشر رسمي' : 'Official Host')}
                                            </span>
                                            <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '2px 0 3px', color: '#FFF' }}>
                                                {activeStreamRoom.title}
                                            </h3>
                                            <p style={{ fontSize: '11px', color: '#CBD5E1', margin: 0 }}>
                                                {isRtl ? "توصيل آمن ومضمون لكافة مناطق المملكة والخليج" : "Complimentary VIP Delivery across GCC & World"}
                                            </p>
                                        </div>

                                        {/* Reactions Trigger Button */}
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            {['💖', '💎', '👑', '🔥'].map(emoji => (
                                                <button
                                                    key={emoji}
                                                    onClick={() => triggerHeart(emoji)}
                                                    style={{
                                                        background: 'rgba(0,0,0,0.65)',
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                        borderRadius: '50%',
                                                        width: '34px',
                                                        height: '34px',
                                                        fontSize: '15px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'transform 0.15s ease'
                                                    }}
                                                    onMouseDown={e => e.currentTarget.style.transform = 'scale(1.25)'}
                                                    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* REAL OFFLINE SCREEN - NO FAKE VIDEO */
                    <div style={{
                        maxWidth: '1100px',
                        margin: '0 auto',
                        background: 'linear-gradient(180deg, rgba(26,20,41,0.5) 0%, rgba(9,10,15,0.9) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '28px',
                        padding: '64px 32px',
                        textAlign: 'center',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.6)'
                    }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px',
                            color: '#FFFFFF'
                        }}>
                            <Radio size={36} />
                        </div>
                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '12px', color: '#FFF' }}>
                            {strings.noStreamTitle}
                        </h2>
                        <p style={{ color: '#94A3B8', fontSize: '15px', maxWidth: '560px', margin: '0 auto 28px', lineHeight: 1.6 }}>
                            {strings.noStreamDesc}
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                background: '#FFFFFF',
                                color: '#090A0F',
                                border: 'none',
                                padding: '12px 28px',
                                borderRadius: '40px',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <span>{strings.browseStore}</span>
                            {isRtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                        </button>
                    </div>
                )}

                {/* REAL CATALOG PRODUCTS SECTION */}
                {liveProducts.length > 0 && (
                    <section style={{ marginTop: '54px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Sparkles size={20} color="#FFFFFF" />
                                    <h2 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0 }}>
                                        {strings.catalogTitle}
                                    </h2>
                                </div>
                                <p style={{ color: '#94A3B8', fontSize: '13px', marginTop: '4px' }}>
                                    {isRtl 
                                        ? `الأسعار معتمدة ومحدثة لعملاء ${selectedRegion.name} بالعملة الرسمية (${selectedRegion.currency})` 
                                        : `${strings.catalogDesc} (${selectedRegion.currency})`}
                                </p>
                            </div>
                        </div>

                        <div className="live-products-grid">
                            {liveProducts.map(product => {
                                const original = Number(product.price) || 0;
                                const displayPrice = original;

                                return (
                                    <div key={product.id} className="live-prod-card">
                                        <div style={{ position: 'relative', height: '190px', borderRadius: '14px', overflow: 'hidden', marginBottom: '14px' }}>
                                            <img
                                                src={product.primaryImageUrl || 'https://luxvono.com/logo512.png'}
                                                alt={product.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => { e.target.src = 'https://luxvono.com/logo512.png'; }}
                                            />
                                            {product.isFeatured && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '10px',
                                                    [isRtl ? 'right' : 'left']: '10px',
                                                    background: '#FFFFFF',
                                                    color: '#090A0F',
                                                    padding: '3px 9px',
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    VIP
                                                </div>
                                            )}
                                        </div>

                                        <h4 style={{ fontSize: '14px', fontWeight: 'bold', margin: '0 0 8px', color: '#FFF', minHeight: '38px', lineHeight: 1.3 }}>
                                            {product.name}
                                        </h4>

                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '14px' }}>
                                            <span style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: '900' }}>
                                                {formatCurrency(displayPrice)}
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                                            <button
                                                onClick={() => {
                                                    if (onAddToCart) onAddToCart(product);
                                                    triggerHeart('💎');
                                                    showToast(`🛒 ${strings.addToCart}: ${product.name}`);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                                    color: '#FFFFFF',
                                                    borderRadius: '10px',
                                                    padding: '9px 10px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {strings.addToCart}
                                            </button>

                                            <button
                                                onClick={() => {
                                                    if (onAddToCart) onAddToCart(product);
                                                    navigate('/cart');
                                                }}
                                                style={{
                                                    flex: 1.2,
                                                    background: '#FFFFFF',
                                                    border: 'none',
                                                    color: '#090A0F',
                                                    borderRadius: '10px',
                                                    padding: '9px 10px',
                                                    fontSize: '12px',
                                                    fontWeight: '900',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {strings.instantBuy}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* SCHEDULE OF UPCOMING PRIVATE EXHIBITIONS */}
                <section style={{ marginTop: '54px', background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '24px', padding: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                        <Sparkles size={20} color="#FFFFFF" />
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                            {strings.scheduleTitle}
                        </h3>
                    </div>

                    {[
                        { title: isRtl ? "أسبوع الموضة في ميلان وباريس - تغطية لوكسفونو الحصرية" : "Milan & Paris Fashion Week - LuxVono Private Coverage", time: isRtl ? "غداً • 8:00 مساءً بتوقيت مكة المكرمة" : "Tomorrow • 8:00 PM GMT+3", host: "Maison LuxVono Haute Couture" },
                        { title: isRtl ? "المزاد المباشر للساعات السويسرية النادرة لكبار الشخصيات" : "VIP Swiss Ultra-Rare Watch Live Showcase", time: isRtl ? "الجمعة القادمة • 9:30 مساءً بتوقيت الرياض" : "Next Friday • 9:30 PM GMT+3", host: "Geneva Master Horologists" }
                    ].map((item, idx) => (
                        <div 
                            key={idx}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '16px 0',
                                borderBottom: idx === 0 ? '1px solid rgba(255,255,255,0.06)' : 'none'
                            }}
                        >
                            <div>
                                <h4 style={{ fontSize: '15px', fontWeight: 'bold', margin: '0 0 4px', color: '#FFF' }}>{item.title}</h4>
                                <span style={{ color: '#FFFFFF', fontSize: '12.5px', fontWeight: '600' }}>{item.time}</span>
                                <span style={{ color: '#64748B', fontSize: '12px', margin: '0 8px' }}>• {item.host}</span>
                            </div>
                            <button
                                onClick={() => showToast(strings.reminderSuccess)}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    color: '#FFFFFF',
                                    padding: '7px 16px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                <Bell size={12} style={{ display: 'inline', margin: '0 4px' }} />
                                {strings.remindMe}
                            </button>
                        </div>
                    ))}
                </section>
            </main>

            {/* COUNTRY / REGION SELECTOR MODAL ("ولبلاد الذي يظهر فيه البث") */}
            {isRegionModalOpen && (
                <div className="live-modal-backdrop" onClick={() => setIsRegionModalOpen(false)}>
                    <div className="live-modal-content" onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <MapPin size={20} color="#FFFFFF" />
                                <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                                    {strings.selectCountryTitle}
                                </h3>
                            </div>
                            <button 
                                onClick={() => setIsRegionModalOpen(false)}
                                style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <p style={{ color: '#94A3B8', fontSize: '13px', marginBottom: '18px', lineHeight: 1.5 }}>
                            {strings.selectCountryDesc}
                        </p>

                        <div style={{ maxHeight: '360px', overflowY: 'auto', paddingRight: '4px' }}>
                            {POPULAR_REGIONS.map(reg => {
                                const isSelected = selectedRegion.code === reg.code;
                                return (
                                    <div
                                        key={reg.code}
                                        className={`live-region-item ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleSelectRegion(reg)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ fontSize: '22px' }}>{reg.flag}</span>
                                            <div>
                                                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                                    {isRtl ? reg.name : reg.nameEn}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                                                    {reg.currency} • {reg.symbol}
                                                </div>
                                            </div>
                                        </div>
                                        {isSelected && <Check size={18} color="#FFFFFF" />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LivePortalPage;
